import gsap from 'gsap';
import Sniffer from 'sniffer';

export default class Transitions {
  constructor() {
    this.type = {
      logo: document.querySelector('.a-logo-link'),
      team: document.querySelectorAll('.s-team-row'),
      menu: document.querySelectorAll('.a-menu-item'),
      slides: document.querySelectorAll('.s-slides-link'),
    };

    this.loader = {
      logo: document.querySelector('.a-loader-logo'),
      container: document.querySelector('.a-loader-container'),
    };

    this.menu = {
      login: document.querySelector('.a-menu-login'),
      masks: document.querySelectorAll('.a-menu-mask'),
      items: document.querySelectorAll('.a-menu-item'),
      trigger: document.querySelector('.a-menu-trigger'),
      active: document.querySelector('.a-menu-item.is-active'),
    };

    this.main = document.querySelector('.a-main');
    this.scene = document.querySelector('.a-scene');
    this.scroll = document.querySelector('.a-scroll');

    this.el = {
      leave: undefined,
      enter: undefined,
    };

    this.done = {
      leave: undefined,
      enter: undefined,
    };

    this.ww = window.innerWidth;
    this.wh = window.innerHeight;

    this.trigger = 'loader';
  }

  /*
  ############
  before enter
  ############
  */

  beforeEnter = (el) => {
    this.el.enter = el;
    this.el.enter.style.zIndex = 1;
    this.el.enter.style.visibility = 'hidden';

    // menu
    if (this.el.enter.querySelector('.s-slides')) {
      gsap.to(this.menu.trigger, {
        opacity: 0,
        pointerEvents: 'none',
      });
    } else {
      gsap.to(this.menu.login, {
        opacity: 0,
        pointerEvents: 'none',
      });
    }
  }

  /*
  #####
  enter
  #####
  */

  enter = (el, done) => {
    this.el.enter = el;
    this.done.enter = done;

    // loader
    if (this.trigger === 'loader') {
      const tl = gsap.timeline({
        defaults: {
          duration: 1.5,
          ease: 'expo.inOut',
        },
      });

      if (Sniffer.isDevice) {
        setTimeout(() => {
          this.scroll.scrollTo(0, 0);
        }, 1500);
      }

      tl
        .to(this.main, {
          y: -window.innerHeight / 2,
        }, 0)

        .set(this.loader.logo, {
          opacity: 0,
          y: -this.wh,
        }, 0)

        .to(this.loader.logo, {
          y: 0,
          opacity: 1,
        }, 0)

        .set(this.loader.container, {
          y: this.wh,
          yPercent: 0,
        }, 0)

        .to(this.loader.container, {
          y: 0,
          onComplete: this.complete,
        }, 0);
    }

    // menu
    if (this.trigger === 'menu') {
      const open = document.querySelector('.a-menu-open');

      const head = this.el.enter.querySelector('.s-header-head');
      const title = this.el.enter.querySelector('.s-header-title');
      const scroll = this.el.enter.querySelector('.s-header-scroll');

      this.complete();

      const tl = gsap.timeline({
        defaults: {
          duration: 1.5,
          ease: 'expo.inOut',
        },
      });

      if (Sniffer.isDevice) {
        this.scroll.scrollTo(0, 0);
      }

      tl
        .set(title, {
          opacity: 0,
          y: -Math.round(this.ww * 0.104),
        }, 0)

        .to(title, {
          y: 0,
          delay: 1,
          opacity: 1,
          clearProps: 'all',
        }, 0)

        .set(open, {
          opacity: 0,
          y: -Math.round(this.ww * 0.104),
        }, 0)

        .to(open, {
          y: 0,
          delay: 1,
          opacity: 1,
        }, 0)

        .set(scroll, {
          opacity: 0,
          y: Math.round(this.ww * 0.104),
        }, 0)

        .to(scroll, {
          y: 0,
          delay: 1,
          opacity: 1,
          clearProps: 'all',
        }, 0)

        .fromTo(head.querySelectorAll('.f-xl-line'), {
          opacity: 0,
          y: this.wh,
        }, {
          y: 0,
          delay: 1,
          opacity: 1,
          stagger: 0.05,
          clearProps: 'all',
        }, 0);
    }

    // slides
    if (this.trigger === 'slides') {
      const head = document.createElement('div');
      const slides = document.querySelector('.s-slides');
      const open = document.querySelector('.a-menu-open');

      const title = this.el.enter.querySelector('.s-header-title');
      const scroll = this.el.enter.querySelector('.s-header-scroll');
      const heading = this.el.enter.querySelector('.s-header-head').innerHTML;

      head.classList.add('s-slides-head');
      head.innerHTML = heading;

      slides.appendChild(head);

      const tl = gsap.timeline({
        defaults: {
          duration: 1.5,
          ease: 'expo.inOut',
        },
      });

      tl
        .set(title, {
          opacity: 0,
          y: -Math.round(this.ww * 0.104),
        }, 0)

        .to(title, {
          y: 0,
          delay: 2,
          opacity: 1,
        }, 0)

        .set(open, {
          opacity: 0,
          y: -Math.round(this.ww * 0.104),
        }, 0)

        .to(open, {
          y: 0,
          delay: 2,
          opacity: 1,
        }, 0)

        .set(scroll, {
          opacity: 0,
          y: Math.round(this.ww * 0.104),
        }, 0)

        .to(scroll, {
          y: 0,
          delay: 2,
          opacity: 1,
        }, 0)

        .fromTo(head.querySelectorAll('.f-xl-line'), {
          opacity: 0,
          y: this.wh,
        }, {
          y: 0,
          delay: 1,
          opacity: 1,
          stagger: 0.05,
          onComplete: this.complete,
        }, 0);
    }

    // team
    if (this.trigger === 'team') {
      const head = this.el.enter.querySelector('.s-bio-head');
      const wrap = this.el.enter.querySelector('.s-bio-wrap');
      const title = this.el.enter.querySelector('.s-bio-title');
      const role = this.el.enter.querySelector('.s-bio-head .f-xs');

      const tl = gsap.timeline({
        defaults: {
          delay: 1.5,
          duration: 1.5,
          ease: 'expo.inOut',
        },
      });

      tl
        .set(title, {
          delay: 0,
          opacity: 0,
          y: -Math.round(this.ww * 0.104),
        }, 0)

        .set(head, {
          delay: 0,
          y: this.wh / 2,
        }, 0)

        .set(role, {
          delay: 0,
          opacity: 0,
        }, 0)

        .set(wrap, {
          delay: 0,
          y: this.wh / 2,
        }, 0)

        .to(title, {
          y: 0,
          opacity: 1,
          clearProps: 'all',
        }, 0)

        .to(head, {
          y: 0,
          clearProps: 'all',
        }, 0)

        .to(role, {
          opacity: 1,
          clearProps: 'all',
        }, 0)

        .to(wrap, {
          y: 0,
          clearProps: 'all',
        }, 0);
    }
  }

  /*
  ###########
  after enter
  ###########
  */

  afterEnter = (el) => {
    this.el.enter = el;
    this.el.enter.removeAttribute('style');

    // menu
    if (this.el.enter.querySelector('.s-slides')) {
      gsap.to(this.menu.login, {
        opacity: 1,
        pointerEvents: '',
      });
    } else {
      gsap.to(this.menu.trigger, {
        opacity: 1,
        pointerEvents: '',
      });
    }

    // loader
    if (this.trigger === 'loader') {
      const tl = gsap.timeline({
        defaults: {
          duration: 1.5,
          ease: 'expo.inOut',
        },
      });

      tl
        .set(this.main, {
          y: window.innerHeight / 2,
        }, 0)

        .to(this.main, {
          y: 0,
          clearProps: 'all',
        }, 0)

        .to(this.loader.logo, {
          opacity: 0,
          y: this.wh,
        }, 0)

        .to(this.loader.container, {
          y: -this.wh,
        }, 0);
    }

    // scene
    if (this.scene) {
      this.scene.remove();
    }
  }

  /*
  ############
  before leave
  ############
  */

  beforeLeave = (el) => {
    this.el.leave = el;
    this.el.leave.style.zIndex = 2;
    this.el.leave.style.pointerEvents = 'none';
  }

  /*
  #####
  leave
  #####
  */

  leave = (el, done) => {
    this.el.leave = el;
    this.done.leave = done;

    // team
    if (this.trigger === 'team') {
      // const image = document.querySelector('.a-scene-image');

      // const width = Math.round(window.innerWidth * 0.29167);
      // const height = Math.round(window.innerWidth * 0.35938);

      // const posX = Math.round(this.ww / 2 - width / 2);
      // const posY = Math.round(this.ww * 0.114);

      const tl = gsap.timeline();

      tl
        .to(this.el.leave, {
          opacity: 0,
          duration: 0.5,
          onComplete: this.complete,
        }, 0)

        .set(this.scroll, {
          y: 0,
          delay: 0.5,
        }, 0);

      // .to(image, {
      //   width,
      //   height,
      //   x: posX,
      //   y: posY,
      //   duration: 1.5,
      //   ease: 'expo.inOut',
      //   onComplete: this.complete,
      // }, 0);
    }
  }

  /*
  ########
  complete
  ########
  */

  complete = () => {
    this.el.enter.style.visibility = 'visible';
    this.done.leave();
    this.done.enter();
  }

  /*
  ####
  active
  ####
  */

  active = () => {
    const active = Array.from(this.menu.items).indexOf(this.menu.active);
    [...this.menu.masks].forEach((el) => {
      el.classList.remove('is-active');
    });
    if (this.menu.masks[active]) {
      this.menu.masks[active].classList.add('is-active');
    }
  }

  /*
  ####
  init
  ####
  */

  init = () => {
    if (this.type.logo) {
      this.type.logo.addEventListener('click', () => {
        this.trigger = 'loader';
      });
    }

    if (this.type.menu) {
      [...this.type.menu].forEach((el) => {
        el.addEventListener('click', () => {
          this.trigger = 'menu';
        });
      });
    }

    if (this.type.team) {
      [...this.type.team].forEach((el) => {
        el.addEventListener('click', () => {
          this.trigger = 'team';
        });
      });
    }

    if (this.type.slides) {
      [...this.type.slides].forEach((el) => {
        el.addEventListener('click', () => {
          this.trigger = 'slides';
        });
      });
    }

    this.active();
  }
}
